<template>
  <div class="ai-assistant-container">
    <div class="row h-100">
      <!-- Chat List Sidebar -->
      <div class="col-md-3 chat-sidebar">
        <div class="sidebar-header">
          <h3 class="mb-0">Aquabot</h3>
          <div class="d-flex justify-content-end align-items-center gap-2">
            <button v-if="$store.getters.userRoles.isAqferAdmin || $store.getters.userRoles.isAqferManager" title="Admin history"
              class="btn btn-outline-dark btn-sm" data-bs-toggle="button" @click="toggleAdminHistory()">
              <i class="fa-solid fa-users"></i>
            </button>
            <button class="btn btn-primary btn-sm" title="New chat" @click="createNewChat()">
              <i class="fa fa-plus"></i>
            </button>
          </div>
        </div>
        <div class="chat-list">
          <!-- Loading state for chat list -->
          <div v-if="loadingChats" class="loading-container">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading conversations...</p>
          </div>

          <!-- Chat list -->
          <template v-else>
            <div v-for="chat in paginatedChats" :key="chat.resourceId" class="chat-item"
              :class="{ active: currentChat?.resourceId === chat.resourceId, otherUserChat: !isChatMine(chat) }" @click="selectChat(chat)">
              <div class="chat-info">
                <div class="chat-title">{{ chat.title || 'New Chat' }}</div>
                <div class="chat-date">
                  {{ formatDate(chat.updatedAt) }}
                  <span v-if="!isChatMine(chat)" class="badge badge-secondary chat-user">{{ chat.userId }}</span>
                </div>
              </div>
              <button v-if="$store.getters.userRoles.isAqferAdmin || isChatMine(chat)" class="delete-chat-btn" @click.stop="deleteChat(chat)" title="Delete conversation">
                <i class="fa fa-trash"></i>
              </button>
            </div>
            <div v-if="chats.length === 0" class="no-chats">
              No conversations yet. Click "New Chat" to start!
            </div>
            
            <!-- Pagination -->
            <div v-if="chats.length > 0 && totalPages > 1" class="chat-pagination">
              <div class="pagination-info">
                Showing {{ startIndex + 1 + "-" + endIndex }} of {{ chats.length }}
              </div>
              <nav aria-label="Chat pagination">
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)" aria-label="Previous">
                      <i class="fa fa-chevron-left"></i>
                    </a>
                  </li>
                  <template v-for="(item, index) in visiblePages" :key="index">
                    <li v-if="item === 'ellipsis'" class="page-item disabled">
                      <span class="page-link">...</span>
                    </li>
                    <li v-else class="page-item" :class="{ active: currentPage === item }">
                      <a class="page-link" href="#" @click.prevent="goToPage(item)">{{ item }}</a>
                    </li>
                  </template>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)" aria-label="Next">
                      <i class="fa fa-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </template>
        </div>
      </div>

      <!-- Chat Area -->
      <div class="col-md-9 chat-area">
        <div v-if="!currentChat" class="empty-state-container">
          <div class="empty-state-content">
            <img src="@/assets/aquabot.jpg" alt="Aquabot" class="aquabot-logo" />
            <h2 class="mb-1">Aquabot Host Application</h2>
            <h5 class="mb-4">MCP CLIENT</h5>
            <h1>How can I help you today?</h1>
          </div>

          <!-- Input Area for Empty State -->
          <div class="empty-input-area">
            <div class="input-wrapper">
              <!-- File attachments preview -->
              <div v-if="attachedFiles.length > 0" class="file-attachments-bar">
                <div v-for="(file, idx) in attachedFiles" :key="idx" class="file-chip" :title="file.name">
                  <i :class="[fileTypeIcon[file.name.split('.').pop()] || 'fa fa-file-alt', 'file-chip-icon']"></i>
                  <span class="file-chip-name">{{ file.name }}</span>
                  <button class="file-chip-remove" @click="removeAttachedFile(idx)" :disabled="isStreaming">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
              <!-- Textarea -->
              <textarea v-model="userInput" @keydown.enter="handleEnter"
                placeholder="Type your message here to start a new chat..." :disabled="isStreaming" rows="1"
                class="form-control input-textarea" ref="emptyMessageInput"></textarea>
              <!-- Toolbar -->
              <div class="input-toolbar">
                <div class="toolbar-actions">
                  <button class="btn btn-sm toolbar-btn" @click="triggerFileSelect" :disabled="isStreaming">
                    <i class="fa fa-paperclip"></i>
                    <span>Attach</span>
                  </button>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="input-hint text-muted">Shift+Enter for new line</span>
                  <button class="btn btn-primary send-button" @click="sendMessage"
                    :disabled="(!userInput.trim() && attachedFiles.length === 0) || isStreaming">
                    <i class="fa fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="chat-container">
          <!-- Messages -->
          <div class="messages-container" ref="messagesContainer">
            <!-- Loading state for messages -->
            <div v-if="loadingMessages" class="loading-container">
              <i class="fa fa-spinner fa-spin fa-2x"></i>
              <p>Loading messages...</p>
            </div>

            <!-- Messages list -->
            <template v-else>
              <div v-for="(message, index) in messages" :key="index" class="message" :class="message.role">
                <div class="message-avatar">
                  <i v-if="message.role === 'user'" class="fa fa-user"></i>
                  <img v-else src="@/assets/aquabot.jpg" alt="Aquabot" class="avatar-image" />
                </div>
                <div class="message-content">
                  <!-- Attached files display for user messages -->
                  <div v-if="message.role === 'user' && getMessageFiles(message).length > 0" class="message-files">
                    <div
                      v-for="(file, fidx) in getMessageFiles(message)"
                      :key="fidx"
                      class="message-file-chip"
                      @click="viewFileContent(message.resourceId, message.messageId, file)"
                      title="Click to view file"
                    >
                      <i :class="[fileTypeIcon[file.name.split('.').pop()] || 'fa fa-file-alt']"></i>
                      <span class="message-file-name">{{ file.name }}</span>
                    </div>
                    <div
                      v-if="getMessageFiles(message).length > 1"
                      :key="fidx"
                      class="message-file-chip-view-all"
                      @click="viewFileContent(message.resourceId, message.messageId)"
                      title="Click to view all files"
                    >
                      <span class="message-file-name">View all</span>
                    </div>
                  </div>
                  <div class="message-text" v-html="renderMarkdown(getMessageText(message))"></div>
                  <!-- UI Resource button - opens flyout panel -->
                  <div v-if="message.uiResources && message.uiResources.length > 0" class="ui-resource-buttons">
                    <button
                      v-for="(resource, idx) in message.uiResources"
                      :key="`${index}-${idx}`"
                      class="btn btn-outline-primary btn-sm ui-resource-btn"
                      @click="openVisualization(resource, index, idx)"
                    >
                      <i class="fa fa-chart-bar me-1"></i>
                      {{ getVisualizationLabel(resource.toolName) }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Streaming Message -->
              <div v-if="isStreaming" class="message assistant">
                <div class="message-avatar">
                  <img src="@/assets/aquabot.jpg" alt="Aquabot" class="avatar-image" />
                </div>
                <div class="message-content">
                  <div v-if="displayStreamingMessage" class="message-text"
                    v-html="renderMarkdown(displayStreamingMessage)"></div>
                  <!-- Streaming UI Resource buttons - auto-open flyout -->
                  <div v-if="streamingUiResources.length > 0" class="ui-resource-buttons">
                    <button
                      v-for="(resource, idx) in streamingUiResources"
                      :key="`streaming-${idx}`"
                      class="btn btn-outline-primary btn-sm ui-resource-btn"
                      :class="{ 'active': isResourceActive(resource, 'streaming', idx) }"
                      @click="openVisualization(resource, 'streaming', idx)"
                    >
                      <i class="fa fa-chart-bar me-1"></i>
                      {{ getVisualizationLabel(resource.toolName) }}
                      <i v-if="!resource.toolResult" class="fa fa-spinner fa-spin ms-1"></i>
                    </button>
                  </div>
                  <div class="streaming-indicator">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Input Area -->
          <div class="input-area">
            <div class="input-wrapper">
              <!-- File attachments preview -->
              <div v-if="attachedFiles.length > 0" class="file-attachments-bar">
                <div v-for="(file, idx) in attachedFiles" :key="idx" class="file-chip" :title="file.name">
                  <i :class="[fileTypeIcon[file.name.split('.').pop()] || 'fa fa-file-alt', 'file-chip-icon']"></i>
                  <span class="file-chip-name">{{ file.name }}</span>
                  <button class="file-chip-remove" @click="removeAttachedFile(idx)" :disabled="isStreaming">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
              <!-- Textarea -->
              <textarea v-model="userInput" @keydown.enter="handleEnter" placeholder="Type your message here..."
                :disabled="!isChatMine(currentChat) || isStreaming" rows="1" class="form-control input-textarea" ref="messageInput"></textarea>
              <!-- Toolbar -->
              <div class="input-toolbar">
                <div class="toolbar-actions">
                  <button class="btn btn-sm toolbar-btn" @click="triggerFileSelect"
                    :disabled="!isChatMine(currentChat) || isStreaming">
                    <i class="fa fa-paperclip"></i>
                    <span>Attach</span>
                  </button>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="input-hint text-muted">Shift+Enter for new line</span>
                  <button class="btn btn-primary send-button" @click="sendMessage"
                    :disabled="!isChatMine(currentChat) || (!userInput.trim() && attachedFiles.length === 0) || isStreaming">
                    <i class="fa fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden file input (shared) -->
    <input type="file" ref="fileInput" multiple @change="handleFileSelect" class="d-none"
      accept=".txt,.csv,.json,.yaml,.yml,.md,.sql,.py,.vue,.js,.ts,.go,.java,.sh,.log,.html,.css,.scss,.avro,.parquet" />

    <!-- Visualization Flyout Panel -->
    <transition name="flyout" @after-enter="onFlyoutEnter" @before-leave="onFlyoutLeave">
      <div v-if="flyoutResource" class="visualization-flyout">
        <div class="flyout-header">
          <h5 class="flyout-title">
            <i class="fa fa-chart-bar me-2"></i>
            {{ getVisualizationLabel(flyoutResource.toolName) }}
          </h5>
          <button class="btn btn-sm btn-close-flyout" @click="closeFlyout" title="Close">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="flyout-content">
          <McpAppRenderer
            v-if="flyoutReady"
            :key="flyoutResource.uri"
            ref="flyoutRenderer"
            :resource-uri="flyoutResource.uri"
            :tool-name="flyoutResource.toolName"
            :tool-input="flyoutResource.toolInput"
            :tool-result="flyoutResource.toolResult"
            :surface-url="agentUrl"
            :access-token="accessToken"
            :cid="currentClient.cid"
            :theme="$store.state.theme || 'light'"
            :min-height="400"
          />
        </div>
      </div>
    </transition>

    <!-- Flyout backdrop -->
    <div v-if="flyoutResource" class="flyout-backdrop" @click="closeFlyout"></div>
  </div>
</template>

<script>
import { marked } from 'marked';
import hljs from 'highlight.js/lib/core';
// Import only the languages you need to reduce bundle size
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import sql from 'highlight.js/lib/languages/sql';
import yaml from 'highlight.js/lib/languages/yaml';
import xml from 'highlight.js/lib/languages/xml';
import 'highlight.js/styles/github-dark.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { mapActions } from 'vuex';
import McpAppRenderer from '@/components/McpAppRenderer.vue';
import { fetchMcpTools, getToolUiResourceUri } from '@/services/surfaceApiMcpAdapter';
import { formatBytes } from '../../utils/commonFunction';
import { getAgentsRequestQueryParams, getAgentsPayloadParams } from '@/utils/agentsApi';

// Register languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('xml', xml);

// Configure marked with custom renderer that includes tool name headers
const renderer = new marked.Renderer();

renderer.code = function (token) {
  // In newer versions of marked, the code renderer receives a token object
  // Extract the code text and language from the token
  const codeStr = token.text || '';
  const lang = token.lang || '';

  // Highlight the code
  let highlighted;
  if (lang && hljs.getLanguage(lang)) {
    try {
      highlighted = hljs.highlight(codeStr, { language: lang }).value;
    } catch (err) {
      console.error('Highlight error:', err);
      highlighted = hljs.highlightAuto(codeStr).value;
    }
  } else {
    try {
      highlighted = hljs.highlightAuto(codeStr).value;
    } catch (err) {
      // Escape HTML manually
      highlighted = codeStr.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  const isSql = lang.toLowerCase() === 'sql';

  // If there's a language/tool name, add a header
  if (lang) {
    return `<div class="code-block-wrapper">
      <div class="code-block-header d-flex justify-content-between align-items-center">
        <span class="code-block-lang">${lang}</span>
        <div>
          ${isSql ? `<div class="d-flex align-items-end gap-2">
            <button class="btn btn-sm btn-dark save-sql" @click="saveQuery" data-code="${encodeURIComponent(codeStr)}" title="Save Query">
              <i class="fa fa-save"></i>
            </button>
            <button class="btn btn-sm btn-success execute-sql" style="color: black;" data-code="${encodeURIComponent(codeStr)}" title="Execute Query">
              <i class="fa fa-play"></i>
            </button>
          </div>` : ''}
        </div>
      </div>
      <pre><code class="hljs language-${lang}">${highlighted}</code></pre>
    </div>`;
  }

  // No language specified, just return the code block
  return `<pre><code class="hljs">${highlighted}</code></pre>`;
};

marked.setOptions({ renderer });

export default {
  name: 'AIAssistant',
  components: {
    McpAppRenderer
  },
  data() {
    return {
      chats: [],
      currentChat: null,
      messages: [],
      userInput: '',
      isStreaming: false,
      streamingMessage: '',
      streamingUiResources: [], // Track UI resources in streaming message
      loadingResources: new Set(), // Track which resources are currently being fetched
      flyoutResource: null, // Currently displayed visualization in flyout
      flyoutResourceKey: null, // Key to track which resource is active (e.g., 'streaming-0' or '5-1')
      flyoutReady: false, // True after flyout transition completes (prevents iframe init during animation)
      agentUrl: null,
      loadingChats: false,
      loadingMessages: false,
      mcpTools: [], // Cache of all MCP tools with metadata
      toolsWithUiMap: {}, // Map of tool name -> UI resource URI

      adminHistory: false,
      currentPage: 1,
      itemsPerPage: 10,

      attachedFiles: [], // Array of { name, content } objects

      sqlClickHandler: null,
      fileTypeIcon: {
        'csv': 'fa-solid fa-file-csv',
        'sql': 'fa-solid fa-database',
        'py': 'fa-brands fa-python',
        'java': 'fa-brands fa-java',
        'js': 'fa-brands fa-square-js',
        'vue': 'fa-brands fa-vuejs',
        'ts': 'fa-brands fa-typescript',
        'go': 'fa-brands fa-golang',
      },
    };
  },
  computed: {
    currentClient() {
      return this.$store.state.currentClient;
    },
    accessToken() {
      return this.$store.state.accessToken;
    },
    displayStreamingMessage() {
      // Return streaming message as-is (no more UI_RESOURCE markers to filter)
      return this.streamingMessage;
    },
    totalPages() {
      return Math.ceil(this.chats.length / this.itemsPerPage);
    },
    paginatedChats() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.chats.slice(start, end);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.chats.length);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 5) {
        // Show all pages if 5 or fewer
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      
      const pages = [];
      
      // Always show first page
      pages.push(1);
      
      // Determine if we need ellipsis on both sides
      const needsEllipsisBefore = current > 3;
      const needsEllipsisAfter = current < total - 2;
      
      if (needsEllipsisBefore && needsEllipsisAfter) {
        // Both ellipsis: show only current page in middle
        // Format: 1 ... current ... last (5 items total)
        pages.push('ellipsis');
        pages.push(current);
        pages.push('ellipsis');
        pages.push(total);
      } else if (needsEllipsisBefore) {
        // Only ellipsis before: show pages near end
        // Format: 1 ... last-2 last-1 last (5 items total)
        pages.push('ellipsis');
        pages.push(total - 2);
        pages.push(total - 1);
        pages.push(total);
      } else if (needsEllipsisAfter) {
        // Only ellipsis after: show pages near start
        // Format: 1 2 3 ... last (5 items total)
        pages.push(2);
        pages.push(3);
        pages.push('ellipsis');
        pages.push(total);
      } else {
        // No ellipsis needed (shouldn't happen with total > 5, but handle it)
        for (let i = 2; i <= total; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    },
  },
  mounted() {
    this.initializeAgentUrl();
    this.loadChats();
    this.setupAutoResize();
    this.loadMcpTools();
    
    // Store handler reference for cleanup
    this.sqlClickHandler = (event) => {
      const saveBtn = event.target.closest('.save-sql');
      const executeBtn = event.target.closest('.execute-sql');

      if (saveBtn && !saveBtn.disabled) {
        const sql = decodeURIComponent(saveBtn.getAttribute('data-code'));
        this.handleSaveQuery(sql);
      } else if (executeBtn && !executeBtn.disabled) {
        const sql = decodeURIComponent(executeBtn.getAttribute('data-code'));
        executeBtn.disabled = true;
        this.handleExecuteQuery(sql);
      }
    };
    document.addEventListener('click', this.sqlClickHandler);
  },
  beforeUnmount() {
    if (this.sqlClickHandler) {
      document.removeEventListener('click', this.sqlClickHandler);
    }
  },
  methods: {
    formatBytes,
    ...mapActions({
      showModal: "showModal"
    }),
    initializeAgentUrl() {
      // Set agent URL based on environment
      // const environment = this.$store.state.environment;
      // switch (environment) {
      //   case 'dev':
      //     this.agentUrl = 'https://ai-agent.api-dev.aqfer.net';
      //     break;
      //   case 'pre_prod':
      //     this.agentUrl = 'https://ai-agent.api-preprod.aqfer.net';
      //     break;
      //   case 'test':
      //     this.agentUrl = 'http://localhost:8080';
      //     break;
      //   default:
      //     this.agentUrl = 'https://ai-agent.api.aqfer.net';
      // }
      // this.agentUrl = 'https://lxlwuwtts3h4643r3k6zow3kg40flndd.lambda-url.us-west-2.on.aws'
      this.agentUrl = this.$store.state.agentsUrl
    },

    initSqlActionListeners() {
      // Use document-level delegation since messagesContainer 
      // may not exist yet (rendered conditionally with v-if)
      document.addEventListener('click', (event) => {
        const saveBtn = event.target.closest('.save-sql');
        const executeBtn = event.target.closest('.execute-sql');

        if (saveBtn && !saveBtn.disabled) {
          const sql = decodeURIComponent(saveBtn.getAttribute('data-code'));
          this.handleSaveQuery(sql);
        } else if (executeBtn && !executeBtn.disabled) {
          const sql = decodeURIComponent(executeBtn.getAttribute('data-code'));
          executeBtn.disabled = true;
          this.handleExecuteQuery(sql);
        }
      });
    },

    handleSaveQuery(sqlQuery) {
      this.showModal({
          component: "AddQuery",
          data: {
              query: {
                  Name: "",
                  Description: "",
                  QueryString: sqlQuery,
                  Internal: false,
                  Params: []
              }
          },
      });
    },

    handleExecuteQuery(sqlQuery) {
      this.userInput = `Execute the following SQL query:\n\n\`\`\`sql\n${sqlQuery.trim()}\n\`\`\``;
      this.sendMessage();
    },

    setupAutoResize() {
      // Auto-resize textarea
      this.$nextTick(() => {
        const textarea = this.$refs.messageInput;
        if (textarea) {
          textarea.addEventListener('input', () => {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
          });
        }
      });
    },

    async loadMcpTools() {
      // Fetch MCP tools with their UI metadata
      try {
        if (!this.agentUrl || !this.accessToken) {
          console.log('Cannot load MCP tools yet - missing agentUrl or accessToken');
          return;
        }

        const result = await fetchMcpTools(this.agentUrl, this.accessToken, this.$store.state, this.currentClient.cid);
        this.mcpTools = result.tools || [];

        // Build map of tool name -> UI resource URI
        this.toolsWithUiMap = {};
        for (const tool of this.mcpTools) {
          const uiUri = getToolUiResourceUri(tool);
          if (uiUri) {
            this.toolsWithUiMap[tool.name] = uiUri;
            console.log(`Tool "${tool.name}" has UI resource: ${uiUri}`);
          }
        }

        console.log(`Loaded ${this.mcpTools.length} MCP tools, ${Object.keys(this.toolsWithUiMap).length} with UI`);
      } catch (error) {
        console.warn('Failed to load MCP tools (non-fatal):', error);
        // Non-fatal error - continue without tool metadata
      }
    },

    async loadChats() {
      this.loadingChats = true;
      try {
        const response = await axios.get(
          `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats`,
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
            params: {
              ...getAgentsRequestQueryParams(this.$store.state),
              ...(this.adminHistory ? { admin_history: true } : {}),
            },
          }
        );
        // Sort chats by updatedAt in descending order (most recent first)
        this.chats = (response.data.items || []).sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt || 0);
          const dateB = new Date(b.updatedAt || b.createdAt || 0);
          return dateB - dateA; // Most recent first
        });
        // Reset to first page when loading new chats
        this.currentPage = 1;
        console.log('Loaded chats from database:', this.chats.map(c => ({ resourceId: c.resourceId, title: c.title })));
      } catch (error) {
        console.error('Failed to load chats:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to load chat history',
        });
      } finally {
        this.loadingChats = false;
      }
    },

    async createNewChat(skipSelect = false) {
      // If skipSelect is false (user clicked "New Chat" button), just clear the UI
      if (!skipSelect) {
        this.currentChat = null;
        this.messages = [];
        this.userInput = '';
        this.attachedFiles = [];
        return;
      }

      // If skipSelect is true, we're creating from sendMessage, so create in DB
      try {
        const resourceId = `chat_${uuidv4()}`;
        const response = await axios.post(
          `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats`,
          {
            resourceId,
            cid: this.currentClient.cid,
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );

        this.chats.unshift(response.data);
        this.currentChat = response.data;

        return response.data;
      } catch (error) {
        console.error('Failed to create chat:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to create new chat',
        });
        throw error;
      }
    },

    async selectChat(chat) {
      this.currentChat = chat;
      this.messages = [];
      await this.loadMessages();
    },

    async deleteChat(chat) {
      // Capture 'this' context for use in the confirm callback
      const self = this;
      
      // Use the Confirm modal instead of native confirm()
      this.showModal({
        component: "confirm",
        data: {
          title: "Delete Chat",
          message: `Are you sure you want to delete "${chat.title || 'New Chat'}"?`,
          confirmText: 'Delete',
          confirmClass: 'btn-danger',
          async confirm() {
            try {
              await axios.delete(
                `${self.agentUrl}/v1/aquabot/cids/${self.currentClient.cid}/chats/${chat.resourceId}`,
                {
                  headers: {
                    'Authorization': `Bearer ${self.accessToken}`,
                  },
                  params: getAgentsRequestQueryParams(self.$store.state),
                }
              );

              // Remove from chat list
              self.chats = self.chats.filter(c => c.resourceId !== chat.resourceId);

              // If we deleted the current chat, clear it
              if (self.currentChat?.resourceId === chat.resourceId) {
                self.currentChat = null;
                self.messages = [];
              }

              self.$store.dispatch('addToast', {
                type: 'success',
                message: 'Chat deleted successfully',
              });
            } catch (error) {
              console.error('Failed to delete chat:', error);
              self.$store.dispatch('addToast', {
                type: 'error',
                message: 'Failed to delete chat',
              });
            }
          }
        }
      });
    },

    async loadMessages() {
      if (!this.currentChat) return;

      this.loadingMessages = true;
      try {
        const response = await axios.get(
          `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats/${this.currentChat.resourceId}/messages`,
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
            params: getAgentsRequestQueryParams(this.$store.state),
          }
        );

        this.messages = response.data || [];

        // Stop loading immediately so messages appear
        this.loadingMessages = false;

        // UI resources are stored with messages in database
        // McpAppRenderer handles rendering them via the uiResources array on each message

        this.scrollToBottom();
      } catch (error) {
        console.error('Failed to load messages:', error);
      } finally {
        this.loadingMessages = false;
      }
    },

    markResourceReady(resourceUri, messageIndex, resourceIndex) {
      // Mark resource as ready - McpAppRenderer handles fetching internally via props
      console.log(`Marking resource ${resourceUri} as ready for message ${messageIndex}, resource ${resourceIndex}`);

      if (this.messages[messageIndex] && this.messages[messageIndex].uiResources[resourceIndex]) {
        this.messages[messageIndex].uiResources[resourceIndex] = {
          uri: resourceUri,
          toolInput: null,
          toolResult: null,
          loading: false
        };
      }
    },

    async sendMessage() {
      const hasText = this.userInput.trim();
      const hasFiles = this.attachedFiles.length > 0;
      if (!hasText && !hasFiles) return;

      // Build the content: object format if files attached, string otherwise
      let messageContent;
      if (hasFiles) {
        messageContent = {
          input: this.userInput.trim(),
          files: [...this.attachedFiles],
        };
      } else {
        messageContent = this.userInput.trim();
      }

      const userMessage = {
        role: 'user',
        content: messageContent,
        timestamp: new Date().toISOString(),
      };
      // Add user message to UI
      this.messages.push(userMessage);
      this.userInput = '';
      this.attachedFiles = [];

      // Create a new chat if one doesn't exist
      if (!this.currentChat) {
        // Reset textarea height
        this.$nextTick(() => {
          const textarea = this.$refs.emptyMessageInput;
          if (textarea) {
            textarea.style.height = 'auto';
          }
        });

        // Create the chat without clearing messages
        await this.createNewChat(true);
      } else {
        // Reset textarea height
        this.$nextTick(() => {
          const textarea = this.$refs.messageInput;
          if (textarea) {
            textarea.style.height = 'auto';
          }
        });
      }

      this.scrollToBottom();

      // Save user message
      try {
        let response = await axios.post(
          `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats/${this.currentChat.resourceId}/messages`,
          {
            messages: [userMessage],
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );

        // Update user message with response content, for file attachments
        console.log('Response data:', response.data);
        if (response?.data?.length > 0) {
          this.messages[this.messages.length - 1] = response.data[0];
        }

        // Stream response
        await this.streamResponse();

        // Generate title if this is the first exchange (user + assistant)
        // After streaming, we should have exactly 2 messages
        console.log('Messages length after streaming:', this.messages.length);
        if (this.messages.length === 2) {
          console.log('Generating title for first message exchange');
          await this.generateTitle();
        } else {
          console.log('Skipping title generation, messages.length =', this.messages.length);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to send message',
        });
      }
    },

    buildSystemPrompt() {
      // Build custom system prompt with user information
      const userInfo = this.$store.state.user || {};
      const clientInfo = this.currentClient || {};

      const systemPrompt = `Today's date is ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.

## Current User Context
- User: ${userInfo.email || 'Unknown'}
- Client: ${clientInfo.name || clientInfo.cid || 'Unknown'} (CID: ${clientInfo.cid || 'N/A'})
${userInfo.name ? `- Full Name: ${userInfo.name}` : ''}

`;

      return systemPrompt;
    },

    async streamResponse() {
      this.isStreaming = true;
      this.streamingMessage = '';
      this.streamingUiResources = [];
      this.detectedResourceUris = new Set(); // Track which resources we've already fetched
      let rawStreamingText = ''; // Keep raw text with markers for extraction

      // Track current tool call for UI detection
      let currentToolName = null;
      let currentToolInput = '';
      let inToolCodeBlock = false;

      try {
        const streamUrl = `${this.agentUrl}/v1/aquabot`;
        const response = await fetch(streamUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resourceId: this.currentChat.resourceId,
            messages: this.messages,
            systemPrompt: this.buildSystemPrompt(),
            cid:this.currentClient.cid,
            ...getAgentsPayloadParams(this.$store.state),
          }),
        });

        if (!response.ok) {
          throw new Error('Streaming failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const forever = true;
        while (forever) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          console.log('Received chunk:', chunk);
          const lines = chunk.split('\n\n');

          for (const line of lines) {
            if (!line.trim()) continue;

            console.log('Processing line:', line);

            const eventMatch = line.match(/^event: (.+)$/m);
            const dataMatch = line.match(/^data: (.+)$/m);

            if (eventMatch && dataMatch) {
              const eventType = eventMatch[1];
              const dataStr = dataMatch[1];

              console.log('Event type:', eventType, 'Data:', dataStr);

              if (eventType === 'text') {
                // Parse JSON data from SSE
                try {
                  const data = JSON.parse(dataStr);
                  console.log('Parsed text data:', data);
                  // Extract the text property from the JSON object
                  if (data.text) {
                    const text = data.text;
                    // Keep raw text with markers for extraction
                    rawStreamingText += text;
                    // Just accumulate text as-is during streaming
                    this.streamingMessage += text;
                    this.scrollToBottom();

                    // Detect tool code block start: ```tool_name
                    const toolStartMatch = text.match(/```(\w+)\n?$/);
                    if (toolStartMatch && !inToolCodeBlock) {
                      const toolName = toolStartMatch[1];
                      // Check if this tool has a UI resource
                      if (this.toolsWithUiMap[toolName]) {
                        console.log(`Tool with UI detected: ${toolName}`);
                        inToolCodeBlock = true;
                        currentToolName = toolName;
                        currentToolInput = '';
                      }
                    }
                    // Accumulate tool input while in code block
                    else if (inToolCodeBlock) {
                      // Check for code block end: ```
                      if (text.includes('```')) {
                        // Extract input before the closing ```
                        const endIdx = text.indexOf('```');
                        currentToolInput += text.substring(0, endIdx);

                        // Parse the tool input and create UI resource
                        try {
                          const toolInput = JSON.parse(currentToolInput.trim());
                          const uiUri = this.toolsWithUiMap[currentToolName];
                          console.log(`Creating UI resource for tool ${currentToolName} with URI ${uiUri}`);
                          this.addUiResource(uiUri, toolInput, null, currentToolName);
                        } catch (parseErr) {
                          console.warn('Could not parse tool input as JSON:', parseErr);
                          // Still create UI resource with raw input
                          const uiUri = this.toolsWithUiMap[currentToolName];
                          this.addUiResource(uiUri, { rawInput: currentToolInput.trim() }, null, currentToolName);
                        }

                        // Reset tool tracking state
                        inToolCodeBlock = false;
                        currentToolName = null;
                        currentToolInput = '';
                      } else {
                        // Still in code block, accumulate input
                        currentToolInput += text;
                      }
                    }
                  }
                } catch (e) {
                  console.error('Failed to parse text data:', e, dataStr);
                }
              } else if (eventType === 'tool_result' || eventType === 'tool' || eventType === 'function_result') {
                // Tool execution result received (support multiple event names)
                try {
                  const data = JSON.parse(dataStr);
                  console.log(`Received ${eventType} event:`, data);

                  // Extract tool name and result from various formats
                  const toolName = data.name || data.tool_name || data.function;
                  const result = data.result || data.output || data.content;

                  if (toolName && result !== undefined) {
                    // Format the result for the MCP App UI
                    const toolResult = {
                      content: result?.content || [{ type: 'text', text: JSON.stringify(result) }],
                      structuredContent: result?.structuredContent || result
                    };
                    this.updateUiResourceToolResult(toolName, toolResult);
                  }
                } catch (e) {
                  console.error(`Failed to parse ${eventType} data:`, e, dataStr);
                }
              } else if (eventType === 'done') {
                // Streaming complete - UI resources are now detected via tool metadata

                // Save assistant message only if we have content
                if (this.streamingMessage && this.streamingMessage.trim()) {
                  const assistantMessage = {
                    role: 'assistant',
                    content: this.streamingMessage,
                    timestamp: new Date().toISOString(),
                    uiResources: [...this.streamingUiResources], // Copy UI resources to message
                  };

                  // If flyout is showing a streaming resource, update reference to the new message
                  if (this.flyoutResourceKey && this.flyoutResourceKey.startsWith('streaming-')) {
                    const streamingIdx = parseInt(this.flyoutResourceKey.split('-')[1]);
                    const newMessageIdx = this.messages.length; // Index after push
                    this.flyoutResourceKey = `${newMessageIdx}-${streamingIdx}`;
                    // Update flyoutResource reference to the one in the message
                    this.flyoutResource = assistantMessage.uiResources[streamingIdx];
                  }

                  // Clear streaming state and add message to array
                  this.streamingMessage = '';
                  this.streamingUiResources = [];
                  this.messages.push(assistantMessage);

                  // Save to database (async, no need to wait)
                  axios.post(
                    `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats/${this.currentChat.resourceId}/messages`,
                    {
                      messages: [assistantMessage],
                      ...getAgentsPayloadParams(this.$store.state),
                    },
                    {
                      headers: {
                        'Authorization': `Bearer ${this.accessToken}`,
                      },
                    }
                  ).catch(err => {
                    console.error('Failed to save assistant message:', err);
                  });
                } else {
                  console.warn('Streaming completed but no message content received');
                  this.streamingMessage = '';
                }
              } else if (eventType === 'error') {
                console.error('Stream error:', dataStr);
                try {
                  const errorData = JSON.parse(dataStr);
                  console.error('Error details:', errorData);
                } catch (e) {
                  // dataStr is not JSON, just log it
                }
                this.$store.dispatch('addToast', {
                  type: 'error',
                  message: 'An error occurred while generating response',
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Streaming error:', error);
        this.$store.dispatch('addToast', {
          type: 'error',
          message: 'Failed to stream response',
        });
      } finally {
        this.isStreaming = false;
        this.scrollToBottom();
      }
    },

    async generateTitle() {
      try {
        console.log('Generating title for chat:', this.currentChat.resourceId);
        const response = await axios.post(
          `${this.agentUrl}/v1/aquabot/cids/${this.currentClient.cid}/chats/${this.currentChat.resourceId}/title`,
          {
            messages: this.messages,
            ...getAgentsPayloadParams(this.$store.state),
          },
          {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
          }
        );

        console.log('Title generation response:', response.data);

        if (response.data.title) {
          this.currentChat.title = response.data.title;
          // Update in chat list
          const chatIndex = this.chats.findIndex(
            c => c.resourceId === this.currentChat.resourceId
          );
          if (chatIndex !== -1) {
            this.chats[chatIndex].title = response.data.title;
            console.log('Title updated in chat list:', response.data.title);
          }
        } else {
          console.warn('No title returned from API');
        }
      } catch (error) {
        console.error('Failed to generate title:', error);
      }
    },

    handleEnter(event) {
      if (!event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
      // Shift+Enter: let the default new-line behavior happen
    },

    getMessageText(message) {
      let text = '';
      if (typeof message.content === 'string') {
        text = message.content;
      } else if (message.content && typeof message.content === 'object' && !Array.isArray(message.content)) {
        // { input, files } format
        text = message.content.input || '';
      } else if (Array.isArray(message.content)) {
        text = message.content
          .map(item => (item.type === 'text' ? item.text : ''))
          .join('');
      }

      return text.trim();
    },

    getMessageFiles(message) {
      if (message.content && typeof message.content === 'object' && (Array.isArray(message.content.files) || Array.isArray(message.content.attachments))) {
        return message.content.files || message.content.attachments;
      }
      return [];
    },

    renderMarkdown(text) {
      if (!text) return '';
      return marked(text);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;

      // Less than 1 minute
      if (diff < 60000) return 'Just now';

      // Less than 1 hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes}m ago`;
      }

      // Less than 1 day
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours}h ago`;
      }

      // Less than 1 week
      if (diff < 604800000) {
        const days = Math.floor(diff / 86400000);
        return `${days}d ago`;
      }

      // Default format
      return date.toLocaleDateString();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    addUiResource(resourceUri, toolInput = null, toolResult = null, toolName = null) {
      // Add UI resource to streaming resources list
      console.log('Adding UI resource:', resourceUri, 'for tool:', toolName);

      const resource = {
        uri: resourceUri,
        toolInput,
        toolResult,
        toolName,
        loading: false
      };

      this.streamingUiResources.push(resource);

      // Auto-open the flyout when a new visualization is ready
      const idx = this.streamingUiResources.length - 1;
      this.openVisualization(resource, 'streaming', idx);

      this.scrollToBottom();
    },

    openVisualization(resource, messageIndex, resourceIndex) {
      // Open the visualization flyout panel
      console.log('Opening visualization:', resource.toolName, 'at', messageIndex, resourceIndex);
      this.flyoutResource = resource;
      this.flyoutResourceKey = `${messageIndex}-${resourceIndex}`;
    },

    closeFlyout() {
      this.flyoutResource = null;
      this.flyoutResourceKey = null;
      this.flyoutReady = false;
    },

    onFlyoutEnter() {
      // Transition complete - safe to mount iframe component
      this.flyoutReady = true;
    },

    onFlyoutLeave() {
      // Reset ready state before leave
      this.flyoutReady = false;
    },

    isResourceActive(resource, messageIndex, resourceIndex) {
      return this.flyoutResourceKey === `${messageIndex}-${resourceIndex}`;
    },

    getVisualizationLabel(toolName) {
      // Return a user-friendly label for the visualization
      const labels = {
        'get_histogram_results': 'Histogram',
        'get_index_value_results': 'Index Values',
        'get-time': 'Server Time',
      };
      return labels[toolName] || 'Visualization';
    },

    updateUiResourceToolResult(toolName, toolResult) {
      // Update the tool result for a pending UI resource
      console.log('Updating UI resource tool result for:', toolName);

      // Find the resource with matching tool name that doesn't have a result yet
      const resourceIndex = this.streamingUiResources.findIndex(
        r => r.toolName === toolName && !r.toolResult
      );

      if (resourceIndex >= 0) {
        // Use Vue's $set for reactivity (Vue 2 compatible)
        const updatedResource = {
          ...this.streamingUiResources[resourceIndex],
          toolResult
        };
        this.$set(this.streamingUiResources, resourceIndex, updatedResource);
        console.log('Updated tool result for resource:', updatedResource.uri);
      } else {
        console.warn('No matching UI resource found for tool result:', toolName);
      }
    },

    triggerFileSelect() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },

    isBinaryFile(file) {
      const binaryExtensions = ['.avro', '.parquet'];
      const lower = file.name.toLowerCase();
      return binaryExtensions.some(ext => lower.endsWith(ext));
    },

    async handleFileSelect(event) {
      const files = Array.from(event.target.files);
      const MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB total limit

      // 1. Calculate size of files ALREADY attached
      const existingTotalSize = this.attachedFiles.reduce((acc, f) => acc + (f.size || 0), 0);

      // 2. Calculate size of the NEW batch
      const newBatchSize = files.reduce((acc, f) => acc + f.size, 0);

      // 3. Check if combined sum exceeds limit
      if (existingTotalSize + newBatchSize > MAX_TOTAL_SIZE) {
        const remainingSpace = formatBytes(MAX_TOTAL_SIZE - existingTotalSize);
        
        this.$store.dispatch('addToast', {
          type: 'error',
          message: `Total attachments exceed 5MB. You have ${remainingSpace} remaining.`,
        });
        
        // Clear input so the same files can be re-selected if curated
        event.target.value = ''; 
        return;
      }

      // 4. Process the files
      for (const file of files) {
        // Avoid duplicates by name
        if (this.attachedFiles.find(f => f.name === file.name)) continue;

        try {
          let content, encoding;
          if (this.isBinaryFile(file)) {
            content = await this.convertFileToBase64(file);
            encoding = 'base64';
          } else {
            content = await this.readFileAsText(file);
            encoding = 'text';
          }

          // Store the original file size so we can calculate the sum later
          this.attachedFiles.push({ 
            name: file.name, 
            content, 
            encoding, 
            size: file.size // Add this property
          });
        } catch (err) {
          this.$store.dispatch('addToast', {
            type: 'error',
            message: `Failed to read "${file.name}"`,
          });
        }
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },

    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // reader.result looks like: "data:application/octet-stream;base64,AAAABBBBCCCC..."
          const base64 = reader.result.split(',')[1];
          resolve(base64); // returns only the Base64 part
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    viewFileContent(rid, mid, file = null) {
      this.showModal({
        component: "AgentInputFileViewer",
        data: {
          title: 'Aquabot - File Viewer',
          resourceId: rid, // chat id
          messageId: mid, // message id
          file: file,
          screensize: 'xl'
        }
      });
    },

    removeAttachedFile(index) {
      this.attachedFiles.splice(index, 1);
    },

    toggleAdminHistory() {
      this.adminHistory = !this.adminHistory;

      // reset the chat and messages
      this.loadChats();
      this.currentChat = null;
      this.messages = [];
    },
    isChatMine(chat) {
      // new chat
      if (!chat) return true;

      // check chat's user id matches current user's email
      return chat.userId === this.$store.state.currentUser.email;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        // Scroll to top of chat list when changing pages
        const chatList = document.querySelector('.chat-list');
        if (chatList) {
          chatList.scrollTop = 0;
        }
      }
    },
  },
};
</script>

<style scoped>
.ai-assistant-container {
  height: 100vh;
  background: #E7EBF2;
}

.row {
  height: 100%;
  margin: 0;
}

/* Sidebar */
.chat-sidebar {
  background: white;
  border-right: 1px solid #dee2e6;
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-header {
  padding: 1.25rem 1rem;
  border-bottom: 2px solid #dee2e6;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.sidebar-header h3 {
  margin: 0;
  font-weight: 700;
  color: #12263F;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
}

.chat-list {
  overflow-y: auto;
  flex: 1;
}

.chat-item {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.chat-item:hover {
  background-color: #f8f9fa;
}

.chat-item.active {
  background-color: #e7f3ff;
  border-left: 3px solid #0d6efd;
}

.chat-item.active.otherUserChat {
  background-color: #f8f9fa;
  border-left: 3px solid #6c757d; /* Grey border for other users' chats */
}

.chat-info {
  flex: 1;
  min-width: 0;
}

.chat-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-date {
  font-size: 0.75rem;
  color: #6c757d;
}

.chat-user {
  font-size: 0.75rem;
  margin-top: 0rem;
}

.delete-chat-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
  opacity: 0;
  flex-shrink: 0;
}

.chat-item:hover .delete-chat-btn {
  opacity: 1;
}

.delete-chat-btn:hover {
  background: #f8d7da;
  color: #dc3545;
}

.no-chats {
  padding: 2rem 1rem;
  text-align: center;
  color: #6c757d;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6c757d;
  gap: 1rem;
}

.loading-container p {
  margin: 0;
  font-size: 0.9rem;
}

/* Chat Area */
.chat-area {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.empty-state-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 1rem 1rem;
}

.empty-state-content {
  text-align: center;
  color: #6c757d;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.empty-state-content i {
  color: #dee2e6;
}

.aquabot-logo {
  width: 150px;
  height: 150px;
  object-fit: contain;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
}

.empty-input-area {
  border-top: 1px solid #dee2e6;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Messages */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1rem;
}

.message {
  display: flex;
  margin-bottom: 1.5rem;
  animation: fadeIn 0.3s;
  width: 100%;
  max-width: 100%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 1rem;
  overflow: hidden;
}

.message.user .message-avatar {
  background: #0d6efd;
  color: white;
}

.message.assistant .message-avatar {
  background: #ffffff;
  border: 1px solid #dee2e6;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.message-text {
  background: #f8f9fa;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.message.user .message-text {
  background: #e7f3ff;
}

.message-text>>>p:last-child {
  margin-bottom: 0;
}

.message-text>>>pre {
  background: #1e1e1e;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 0.75rem 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.message-text>>>pre code {
  background: transparent;
  padding: 0;
  color: #d4d4d4;
  font-family: 'Courier New', Consolas, Monaco, monospace;
  font-size: 0.875rem;
  line-height: 1.5;
}

.message-text>>>code {
  background: transparent;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-family: 'Courier New', Consolas, Monaco, monospace;
  font-size: 0.875rem;
}

.message-text>>>.hljs {
  background: #1e1e1e;
  color: #d4d4d4;
}

/* Table styles */
.message-text>>>table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  overflow: hidden;
}

.message-text>>>thead {
  background: linear-gradient(135deg, #8a9cad 0%, #7a8c9d 100%);
  color: white;
}

.message-text>>>thead th {
  padding: 0.625rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #7a8c9d;
}

.message-text>>>tbody tr {
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.message-text>>>tbody tr:last-child {
  border-bottom: none;
}

.message-text>>>tbody tr:hover {
  background-color: #f8f9fa;
}

.message-text>>>tbody td {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #495057;
  vertical-align: top;
}

.message-text>>>tbody tr:nth-child(even) {
  background-color: #fafbfc;
}

.message-text>>>tbody tr:nth-child(even):hover {
  background-color: #f1f3f5;
}

/* Code block wrapper with header for tool names */
.message-text>>>.code-block-wrapper {
  margin: 0.75rem 0;
  border-radius: 0.5rem;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.message-text>>>.code-block-header {
  background: #464647;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #1e1e1e;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message-text>>>.code-block-lang {
  color: #d4d4d4;
  font-size: 0.75rem;
  font-family: 'Courier New', Consolas, Monaco, monospace;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.message-text>>>.code-block-wrapper pre {
  margin: 0;
  border-radius: 0;
}

.streaming-indicator {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.streaming-indicator .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6c757d;
  animation: pulse 1.5s infinite;
}

.streaming-indicator .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.streaming-indicator .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {

  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }

  30% {
    opacity: 1;
    transform: scale(1);
  }
}

/* UI Resources */
.ui-resources {
  margin-top: 0.75rem;
}

.ui-resource-container {
  margin-bottom: 0.75rem;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: white;
  min-height: 300px;
}

.ui-resource-container:last-child {
  margin-bottom: 0;
}

/* Ensure ui-resource-renderer takes full height */
.ui-resource-container ui-resource-renderer {
  display: block;
  width: 100%;
  min-height: 300px;
  height: 100%;
}

/* Target shadow DOM content inside ui-resource-renderer */
.ui-resource-container ui-resource-renderer::part(container) {
  min-height: 300px;
  height: 100%;
}

/* Use deep selector for shadow DOM penetration */
.ui-resource-container>>>ui-resource-renderer,
.ui-resource-container ::v-deep ui-resource-renderer {
  min-height: 300px;
}

.ui-resource-container>>>ui-resource-renderer>*,
.ui-resource-container ::v-deep ui-resource-renderer>* {
  min-height: 300px;
  height: 100%;
}

.ui-resource-container>>>ui-resource-renderer iframe,
.ui-resource-container ::v-deep ui-resource-renderer iframe {
  min-height: 300px;
  height: 100%;
  width: 100%;
  border: none;
}

.resource-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  background: white;
  color: #6c757d;
  gap: 1rem;
}

.resource-loading p {
  margin: 0;
  font-size: 0.9rem;
}

/* UI Resource Buttons */
.ui-resource-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.ui-resource-btn {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.ui-resource-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.ui-resource-btn.active {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

/* Visualization Flyout Panel */
.visualization-flyout {
  position: fixed;
  top: 0;
  right: 0;
  width: 50%;
  max-width: 800px;
  min-width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.flyout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.flyout-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.btn-close-flyout {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close-flyout:hover {
  background: #f0f0f0;
  color: #333;
}

.flyout-content {
  flex: 1;
  overflow: auto;
  padding: 0;
}

.flyout-content .mcp-app-container {
  height: 100%;
  min-height: 100%;
}

/* Flyout backdrop */
.flyout-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1040;
}

/* Flyout transitions */
.flyout-enter-active,
.flyout-leave-active {
  transition: transform 0.3s ease;
}

.flyout-enter-from,
.flyout-leave-to {
  transform: translateX(100%);
}

/* Responsive: full width on smaller screens */
@media (max-width: 992px) {
  .visualization-flyout {
    width: 100%;
    max-width: none;
    min-width: auto;
  }
}

/* Input Area */
.input-area {
  border-top: 1px solid #dee2e6;
  padding: 1rem;
  background: white;
}

.input-wrapper {
  border: 1.5px solid #dee2e6;
  border-radius: 0.75rem;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.input-wrapper:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
}

/* File attachments bar */
.file-attachments-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem 0;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  /* background: #eaf1ff; */
  background: #e7f3ff;
  border: 1px solid #2C7BE5;
  border-radius: 20px;
  padding: 0.25rem 0.5rem 0.25rem 0.6rem;
  font-size: 0.8rem;
  color: #2C7BE5;
  max-width: 200px;
}

.file-chip-icon {
  font-size: 0.75rem;
  flex-shrink: 0;
}

.file-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
}

.file-chip-remove {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 0.7rem;
  transition: background 0.15s, color 0.15s;
}

.file-chip-remove:hover {
  background: rgba(255, 0, 21, 0.1);
  color: #dc3545;
}

/* Textarea inside wrapper */
.input-textarea {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  resize: none;
  max-height: 200px;
  min-height: 44px;
  padding: 0.75rem;
  background: transparent !important;
}

.input-textarea:focus {
  outline: none;
  box-shadow: none !important;
}

/* Toolbar row */
.input-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.6rem 0.4rem 0.5rem;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #6c757d;
  background: none;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  transition: all 0.15s;
}

.toolbar-btn:hover:not(:disabled) {
  background: #f0f0f0;
  border-color: #dee2e6;
  color: #333;
}

.toolbar-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.input-hint {
  font-size: 0.72rem;
  white-space: nowrap;
}

.send-button {
  height: 36px;
  width: 36px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.85rem;
}

/* Attached files shown in sent user messages */
.message-files {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
}

.message-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #e7f3ff;
  border: 1px solid #2C7BE5;
  border-radius: 20px;
  padding: 0.2rem 0.65rem;
  font-size: 0.78rem;
  color: #2C7BE5;
  max-width: 220px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.message-file-chip:hover {
  background: #d2e8ff;
  border-color: #2C7BE5;
}

.message-file-chip:active {
  background: #b6cfff;
}

.message-file-chip-view-all {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: var(--bs-primary);
  border-radius: 20px;
  padding: 0.2rem 0.65rem;
  font-size: 0.78rem;
  color: #ffffff;
  cursor: pointer;
}

.message-file-chip-view-all:hover {
  background: #116ce3;
}

.message-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}

/* Scrollbar */
.messages-container::-webkit-scrollbar,
.chat-list::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track,
.chat-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb,
.chat-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover,
.chat-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.chat-pagination {
  padding: 1rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  background: white;
}

.pagination-info {
  font-size: 0.75rem;
  color: #6c757d;
}

.chat-pagination .pagination {
  margin: 0;
}

.chat-pagination .page-link {
  color: #6c757d;
  border-color: #dee2e6;
  padding: 0.375rem 0.75rem;
}

.chat-pagination .page-link:hover {
  color: #0d6efd;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.chat-pagination .page-item.active .page-link {
  background-color: #0d6efd !important;
  border-color: #0d6efd !important;
  color: white !important;
}

.chat-pagination .page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  cursor: auto;
  background-color: #fff;
  border-color: #dee2e6;
  opacity: 0.5;
}

.message-text >>> .execute-sql:disabled,
.message-text >>> .save-sql:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.message.user .message-text >>> .code-block-header {
  display: none !important;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;
body.dark-mode {
  .chat-pagination {
    color: #a0a4b8 !important;
    border-color: #2e3238 !important;
    background-color: #1a1d23 !important;
  }

  .empty-state-container {
    background-color: #1a1d23 !important;
    // background-color: #111317 !important;
  }

  .chat-item {
        &:hover {
            background-color: $dark-hover-bg !important;
            color: $dark-text-primary !important;
        }
        &.active {
            background-color: $dark-selected-bg !important;
            color: $dark-text-primary !important;
        }
    }

    .chat-item {
        border-bottom-color: $dark-border-color !important;
    }

    .input-area {
        border-top-color: $dark-border-color !important;
        background-color: $dark-card-bg !important;
    }

    .empty-input-area {
        border-top-color: $dark-border-color !important;
        background-color: $dark-card-bg !important;
    }

    .chat-container {
        background-color: $dark-card-bg !important;
    }

    .messages-container {
        background-color: $dark-card-bg !important;
    }

    .message-text {
        background-color: $dark-elevated-bg !important;
    }

    .message.user .message-text {
      background: rgb(42, 58, 80) !important
    }

    .input-wrapper {
      border-color: $dark-border-color !important;
      background-color: $dark-elevated-bg !important;
    }

    .input-wrapper:focus-within {
      border-color: #4d7cfe !important;
      box-shadow: 0 0 0 3px rgba(77, 124, 254, 0.15) !important;
    }

    .input-textarea {
      background-color: transparent !important;
      color: $dark-text-primary !important;
    }

    .input-toolbar {
      background-color: $dark-card-bg !important;
      border-top-color: $dark-border-color !important;
    }

    .toolbar-btn {
      color: #a0a4b8 !important;
    }

    .toolbar-btn:hover:not(:disabled) {
      background: $dark-hover-bg !important;
      border-color: $dark-border-color !important;
      color: $dark-text-primary !important;
    }

    .file-chip {
      background: $dark-body-bg !important;
      border-color: $dark-accent !important;
      color: $dark-accent !important;
    }

    .message-file-chip {
      background: $dark-body-bg !important;
      border-color: $dark-accent !important;
      color: $dark-accent !important;
    }

    .message-file-chip:hover {
      background: rgb(23, 39, 63) !important;
    }

    .message-file-chip-view-all {
      color: $dark-body-bg !important;
    }

    .message-text table {
        background-color: $dark-elevated-bg !important;
    }

    .message-text thead th {
        background: #4e5051 !important;
        border: none !important;
        border-right: 1px solid #2e3238 !important;
        color: white !important;
    }

    .message-text tbody tr:nth-child(even) {
        background-color: $dark-elevated-bg !important;
    }
    
    .message-text tbody tr:nth-child(even):hover {
        background-color: $dark-hover-bg !important;
    }

    .message-text tbody td {
        background-color: $dark-elevated-bg !important;
        color: $dark-text-primary !important;
    }

    .message-text tbody tr {
        border-bottom: 1px solid #4e5051 !important;
    }
    .message-text tbody tr:last-child {
        border-bottom: none !important;
    }

    .message-text .code-block-header {
        background: #464647 !important;
    }

    .delete-chat-btn:hover {
      background: #ff00151c;
      color: #dc3545;
    }
}
</style>